import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Tv,
  Gamepad,
  Video,
  Briefcase,
  Users,
  Smartphone,
  Laptop,
  Tablet,
  Monitor,
  Download,
  Upload,
  Cloud,
  ArrowRight,
  ArrowLeft,
  Check
} from 'lucide-react';
import useComparisonStore from '../store/comparisonStore';
import { stepTransitionVariants, contentVariants, buttonHoverVariants } from '../utils/animations';
import { getRecommendationExplanation } from '../utils/formatters';
import { trackEvent, trackJourneyStep } from '../../../config/clarity';

/**
 * RecommendationForm component
 * Multi-step form to gather user preferences for speed recommendations
 */
const RecommendationForm = ({ onComplete = () => {} }) => {
  const {
    recommendationAnswers,
    updateRecommendationAnswers,
    recommendedSpeed,
    handleRecommendationComplete: completeRecommendation,
    proceedToSelectionFromRecommendation
  } = useComparisonStore();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(0);
  
  // Define the steps in the recommendation process
  const steps = [
    {
      id: 'usage-type',
      title: 'What will you primarily use your internet for?',
      component: UsageTypeStep
    },
    {
      id: 'users',
      title: 'How many people will use this internet connection?',
      component: UsersStep
    },
    {
      id: 'devices',
      title: 'How many devices will be connected?',
      component: DevicesStep
    },
    {
      id: 'activities',
      title: 'Which activities do you regularly perform?',
      component: ActivitiesStep
    },
    {
      id: 'result',
      title: 'Your Recommended Speed',
      component: ResultStep
    }
  ];
  
  // Go to the next step
  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      // Track step navigation
      trackEvent('recommendation_step_next', {
        fromStep: steps[currentStep].id,
        toStep: steps[currentStep + 1].id,
        stepNumber: currentStep + 1
      });
      
      setDirection(1);
      setCurrentStep(currentStep + 1);
    }
  };
  
  // Go to the previous step
  const prevStep = () => {
    if (currentStep > 0) {
      // Track step navigation
      trackEvent('recommendation_step_back', {
        fromStep: steps[currentStep].id,
        toStep: steps[currentStep - 1].id,
        stepNumber: currentStep - 1
      });
      
      setDirection(-1);
      setCurrentStep(currentStep - 1);
    }
  };
  
  // Complete the recommendation process
  const handleComplete = () => {
    // Track recommendation completion
    trackEvent('recommendation_completed', {
      recommendedSpeed,
      answers: JSON.stringify(recommendationAnswers)
    });
    
    // Track journey step
    trackJourneyStep('package_selection', 'received_recommendation', {
      recommendedSpeed,
      usageTypes: recommendationAnswers.usageTypes?.join(',') || 'none',
      usageIntensity: recommendationAnswers.usageIntensity || 'moderate',
      users: recommendationAnswers.users || 0,
      devices: recommendationAnswers.devices || 0
    });
    
    completeRecommendation();
    // Pass the recommended speed to the onComplete function
    onComplete(recommendedSpeed);
  };
  
  // Current step component
  const StepComponent = steps[currentStep].component;
  
  return (
    <div className="flex flex-col h-full">
      {/* Progress indicator */}
      <div className="mb-6">
        <div className="flex justify-between mb-2">
          {steps.map((step, index) => (
            <motion.div 
              key={index}
              className={`h-2 rounded-full ${
                index === currentStep 
                  ? 'bg-purple-500 w-8' 
                  : index < currentStep 
                    ? 'bg-purple-500 w-4' 
                    : 'bg-gray-700 w-4'
              }`}
              initial={false}
              animate={{ 
                width: index === currentStep ? 32 : 16,
                backgroundColor: index <= currentStep ? '#a855f7' : '#374151'
              }}
              transition={{ duration: 0.3 }}
            />
          ))}
        </div>
        <h2 className="text-lg font-semibold text-white">{steps[currentStep].title}</h2>
      </div>
      
      {/* Step content with animations */}
      <div className="flex-1 overflow-hidden">
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            variants={stepTransitionVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="h-full"
          >
            <StepComponent 
              updateAnswers={updateRecommendationAnswers}
              answers={recommendationAnswers}
              recommendedSpeed={recommendedSpeed}
              onComplete={handleComplete}
            />
          </motion.div>
        </AnimatePresence>
      </div>
      
      {/* Navigation buttons */}
      <div className="mt-6 flex justify-between">
        {currentStep > 0 && currentStep < steps.length - 1 ? (
          <motion.button
            className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm font-medium flex items-center"
            onClick={prevStep}
            variants={buttonHoverVariants}
            initial="initial"
            whileHover="hover"
            whileTap="tap"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </motion.button>
        ) : (
          <div></div> // Empty div to maintain layout
        )}
        
        {currentStep < steps.length - 1 && (
          <motion.button
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium flex items-center"
            onClick={nextStep}
            variants={buttonHoverVariants}
            initial="initial"
            whileHover="hover"
            whileTap="tap"
          >
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </motion.button>
        )}
      </div>
    </div>
  );
};

// Step 1: Usage Type Selection
const UsageTypeStep = ({ updateAnswers, answers }) => {
  const usageTypes = [
    {
      id: 'general',
      label: 'General Browsing',
      description: 'Web browsing, email, social media',
      icon: <Laptop className="h-6 w-6" />
    },
    {
      id: 'streaming',
      label: 'Streaming',
      description: 'Netflix, YouTube, Disney+',
      icon: <Tv className="h-6 w-6" />
    },
    {
      id: 'gaming',
      label: 'Gaming',
      description: 'Online gaming, game downloads',
      icon: <Gamepad className="h-6 w-6" />
    },
    {
      id: 'work',
      label: 'Work From Home',
      description: 'Video calls, file sharing, remote access',
      icon: <Briefcase className="h-6 w-6" />
    }
  ];
  
  // Add intensity levels for usage
  const intensityLevels = [
    { id: 'light', label: 'Light', description: 'Occasional use' },
    { id: 'moderate', label: 'Moderate', description: 'Regular use' },
    { id: 'heavy', label: 'Heavy', description: 'Intensive use' }
  ];
  
  // Initialize the usageTypes array if it doesn't exist
  const selectedUsageTypes = answers.usageTypes || [];
  
  const handleToggleUsageType = (usageType) => {
    // Get current selection
    const currentSelection = [...selectedUsageTypes];
    
    // Check if this type is already selected
    const index = currentSelection.indexOf(usageType);
    
    if (index === -1) {
      // Not selected, so add it if we have less than 2 selections
      if (currentSelection.length < 2) {
        // Track usage type selection
        trackEvent('recommendation_usage_type_selected', {
          usageType,
          currentSelectionCount: currentSelection.length + 1
        });
        
        const newSelection = [...currentSelection, usageType];
        
        // If no intensity is selected yet, set a default of 'moderate'
        if (!answers.usageIntensity) {
          updateAnswers({ usageTypes: newSelection, usageIntensity: 'moderate' });
        } else {
          updateAnswers({ usageTypes: newSelection });
        }
      }
    } else {
      // Already selected, so remove it
      // Track usage type deselection
      trackEvent('recommendation_usage_type_deselected', {
        usageType,
        currentSelectionCount: currentSelection.length - 1
      });
      
      currentSelection.splice(index, 1);
      updateAnswers({ usageTypes: currentSelection });
    }
  };
  
  const handleIntensitySelect = (intensity) => {
    // Track intensity selection
    trackEvent('recommendation_intensity_selected', {
      intensity,
      previousIntensity: answers.usageIntensity || 'none'
    });
    
    updateAnswers({ usageIntensity: intensity });
  };
  
  return (
    <motion.div
      className="space-y-6"
      variants={contentVariants}
    >
      <div className="mb-2 text-sm text-gray-300 flex justify-between items-center">
        <span>Select up to 2 primary uses for your internet:</span>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          selectedUsageTypes.length === 2 ? 'bg-green-600/20 text-green-400' : 'bg-gray-700 text-gray-300'
        }`}>
          {selectedUsageTypes.length}/2 selected
        </span>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {usageTypes.map((type) => {
          const isSelected = selectedUsageTypes.includes(type.id);
          const isDisabled = !isSelected && selectedUsageTypes.length >= 2;
          
          return (
            <motion.button
              key={type.id}
              className={`p-4 rounded-xl flex items-start ${
                isSelected
                  ? 'bg-purple-600 text-white'
                  : isDisabled
                    ? 'bg-gray-800/50 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
              onClick={() => !isDisabled && handleToggleUsageType(type.id)}
              whileHover={{ scale: isDisabled ? 1 : 1.02 }}
              whileTap={{ scale: isDisabled ? 1 : 0.98 }}
            >
              <div className={`p-2 rounded-full mr-3 ${
                isSelected
                  ? 'bg-purple-500/30'
                  : 'bg-gray-700'
              }`}>
                {type.icon}
              </div>
              <div className="text-left">
                <div className="font-medium">{type.label}</div>
                <div className={`text-xs mt-1 ${
                  isSelected
                    ? 'text-purple-200'
                    : isDisabled
                      ? 'text-gray-500'
                      : 'text-gray-400'
                }`}>
                  {type.description}
                </div>
              </div>
              {isSelected && (
                <Check className="h-5 w-5 ml-auto" />
              )}
            </motion.button>
          );
        })}
      </div>
      
      {/* Only show intensity selection if at least one usage type is selected */}
      {selectedUsageTypes.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-medium text-gray-300 mb-3">How intensively do you use the internet?</h3>
          <div className="flex space-x-3">
            {intensityLevels.map((level) => (
              <motion.button
                key={level.id}
                className={`flex-1 p-3 rounded-lg ${
                  answers.usageIntensity === level.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
                onClick={() => handleIntensitySelect(level.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="font-medium">{level.label}</div>
                <div className={`text-xs mt-1 ${
                  answers.usageIntensity === level.id
                    ? 'text-purple-200'
                    : 'text-gray-400'
                }`}>
                  {level.description}
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Step 2: Number of Users
const UsersStep = ({ updateAnswers, answers }) => {
  const userOptions = [1, 2, 3, 4, 5, '6+'];
  
  const handleSelect = (users) => {
    // Convert '6+' to 6 for calculation purposes
    const userCount = users === '6+' ? 6 : users;
    
    // Track user count selection
    trackEvent('recommendation_users_selected', {
      userCount,
      previousUserCount: answers.users || 0
    });
    
    updateAnswers({ users: userCount });
  };
  
  return (
    <motion.div 
      className="space-y-6"
      variants={contentVariants}
    >
      <div className="flex justify-center mb-6">
        <div className="p-4 bg-purple-600/20 rounded-full">
          <Users className="h-12 w-12 text-purple-400" />
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-3">
        {userOptions.map((option) => (
          <motion.button
            key={option}
            className={`py-3 px-4 rounded-lg ${
              answers.users === (option === '6+' ? 6 : option)
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            onClick={() => handleSelect(option)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {option}
          </motion.button>
        ))}
      </div>
      
      <p className="text-sm text-gray-400 text-center mt-4">
        More users means more devices potentially using the internet at the same time.
      </p>
    </motion.div>
  );
};

// Step 3: Number of Devices
const DevicesStep = ({ updateAnswers, answers }) => {
  const deviceOptions = [
    { id: 'few', label: '1-3 devices', count: 2 },
    { id: 'several', label: '4-6 devices', count: 5 },
    { id: 'many', label: '7-10 devices', count: 8 },
    { id: 'lots', label: '10+ devices', count: 12 }
  ];
  
  const deviceIcons = [
    <Smartphone className="h-5 w-5 text-purple-400" />,
    <Laptop className="h-5 w-5 text-purple-400" />,
    <Tablet className="h-5 w-5 text-purple-400" />,
    <Tv className="h-5 w-5 text-purple-400" />,
    <Monitor className="h-5 w-5 text-purple-400" />
  ];
  
  const handleSelect = (deviceCount) => {
    // Track device count selection
    trackEvent('recommendation_devices_selected', {
      deviceCount,
      previousDeviceCount: answers.devices || 0
    });
    
    updateAnswers({ devices: deviceCount });
  };
  
  return (
    <motion.div 
      className="space-y-4"
      variants={contentVariants}
    >
      <div className="flex justify-center gap-2 mb-6">
        {deviceIcons.map((icon, index) => (
          <div 
            key={index} 
            className="p-2 bg-gray-800 rounded-lg"
          >
            {icon}
          </div>
        ))}
      </div>
      
      <div className="space-y-3">
        {deviceOptions.map((option) => (
          <motion.button
            key={option.id}
            className={`w-full py-3 px-4 rounded-lg flex items-center justify-between ${
              answers.devices === option.count
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            onClick={() => handleSelect(option.count)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span>{option.label}</span>
            {answers.devices === option.count && (
              <Check className="h-5 w-5" />
            )}
          </motion.button>
        ))}
      </div>
      
      <p className="text-sm text-gray-400 text-center mt-4">
        Consider all smartphones, laptops, tablets, smart TVs, and other connected devices.
      </p>
    </motion.div>
  );
};

// Step 4: Activities
const ActivitiesStep = ({ updateAnswers, answers }) => {
  const activities = [
    { 
      id: '4k-streaming', 
      label: '4K Streaming', 
      description: 'Ultra HD video streaming',
      icon: <Tv className="h-5 w-5" />
    },
    { 
      id: 'video-calls', 
      label: 'Video Conferencing', 
      description: 'Zoom, Teams, Google Meet',
      icon: <Video className="h-5 w-5" />
    },
    { 
      id: 'large-downloads', 
      label: 'Large Downloads', 
      description: 'Games, software, large files',
      icon: <Download className="h-5 w-5" />
    },
    { 
      id: 'cloud-backup', 
      label: 'Cloud Backup', 
      description: 'Backing up photos, videos, files',
      icon: <Cloud className="h-5 w-5" />
    },
    { 
      id: 'file-sharing', 
      label: 'File Sharing', 
      description: 'Sending large files to others',
      icon: <Upload className="h-5 w-5" />
    }
  ];
  
  const toggleActivity = (activityId) => {
    const currentActivities = answers.activities || [];
    
    if (currentActivities.includes(activityId)) {
      // Track activity deselection
      trackEvent('recommendation_activity_deselected', {
        activity: activityId,
        currentActivitiesCount: currentActivities.length - 1
      });
      
      // Remove activity
      updateAnswers({
        activities: currentActivities.filter(id => id !== activityId)
      });
    } else {
      // Track activity selection
      trackEvent('recommendation_activity_selected', {
        activity: activityId,
        currentActivitiesCount: currentActivities.length + 1
      });
      
      // Add activity
      updateAnswers({
        activities: [...currentActivities, activityId]
      });
    }
  };
  
  return (
    <motion.div 
      className="space-y-3"
      variants={contentVariants}
    >
      <p className="text-sm text-gray-400 mb-4">
        Select all activities that apply to your household:
      </p>
      
      {activities.map((activity) => {
        const isSelected = (answers.activities || []).includes(activity.id);
        
        return (
          <motion.button
            key={activity.id}
            className={`w-full py-3 px-4 rounded-lg flex items-center ${
              isSelected
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            onClick={() => toggleActivity(activity.id)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className={`p-1.5 rounded-full mr-3 ${
              isSelected
                ? 'bg-purple-500/30'
                : 'bg-gray-700'
            }`}>
              {activity.icon}
            </div>
            <div className="text-left">
              <div className="font-medium">{activity.label}</div>
              <div className={`text-xs mt-0.5 ${
                isSelected
                  ? 'text-purple-200'
                  : 'text-gray-400'
              }`}>
                {activity.description}
              </div>
            </div>
            {isSelected && (
              <Check className="h-5 w-5 ml-auto" />
            )}
          </motion.button>
        );
      })}
    </motion.div>
  );
};

// Step 5: Result
const ResultStep = ({ recommendedSpeed, answers, onComplete }) => {
  const { proceedToSelectionFromRecommendation } = useComparisonStore();
  const explanation = getRecommendationExplanation(recommendedSpeed, answers);
  
  // Function to handle the completion with the recommended speed
  const handleCompleteWithSpeed = () => {
    // Track recommendation completion and proceed action
    trackEvent('recommendation_proceed_to_packages', {
      recommendedSpeed,
      usageTypes: answers.usageTypes?.join(',') || 'none',
      usageIntensity: answers.usageIntensity || 'moderate',
      users: answers.users || 0,
      devices: answers.devices || 0,
      activities: answers.activities?.join(',') || 'none'
    });
    
    // Track journey step
    trackJourneyStep('package_selection', 'viewing_recommended_packages', {
      recommendedSpeed
    });
    
    // First update the recommendation speed
    onComplete(recommendedSpeed);
    
    console.log("User explicitly clicked 'Show Recommended Packages' button");
    
    // Then proceed to selection modal ONLY because the user explicitly clicked the button
    // This is an explicit user action, not an automatic transition
    proceedToSelectionFromRecommendation();
  };
  
  return (
    <motion.div
      className="space-y-6"
      variants={contentVariants}
    >
      <div className="flex justify-center mb-4">
        <div className="p-4 bg-green-600/20 rounded-full">
          <Check className="h-12 w-12 text-green-400" />
        </div>
      </div>
      
      <div className="text-center">
        <h3 className="text-2xl font-bold text-white mb-2">
          {recommendedSpeed} Mbps
        </h3>
        <p className="text-gray-300">
          {explanation}
        </p>
      </div>
      
      <div className="bg-gray-800 rounded-lg p-4 mt-6">
        <h4 className="text-sm font-medium text-white mb-2">What this means:</h4>
        <ul className="space-y-2 text-sm text-gray-300">
          <li className="flex items-start">
            <Check className="h-4 w-4 text-green-400 mr-2 mt-0.5" />
            <span>We'll show you packages with speeds around {recommendedSpeed} Mbps</span>
          </li>
          <li className="flex items-start">
            <Check className="h-4 w-4 text-green-400 mr-2 mt-0.5" />
            <span>This speed should handle {answers.users} users with {answers.devices} devices</span>
          </li>
          <li className="flex items-start">
            <Check className="h-4 w-4 text-green-400 mr-2 mt-0.5" />
            <span>
              {answers.usageTypes && answers.usageTypes.length > 0 ? (
                <>
                  Perfect for {answers.usageTypes.map((type, index) => {
                    const usageLabel =
                      type === 'general' ? 'everyday browsing' :
                      type === 'streaming' ? 'streaming content' :
                      type === 'gaming' ? 'online gaming' : 'working from home';
                    
                    return (
                      <span key={type}>
                        {index > 0 && ' and '}
                        {usageLabel}
                      </span>
                    );
                  })}
                </>
              ) : (
                'Perfect for your internet needs'
              )}
              {answers.usageIntensity && ` with ${answers.usageIntensity} usage intensity`}
            </span>
          </li>
          {answers.activities && answers.activities.length > 0 && (
            <li className="flex items-start">
              <Check className="h-4 w-4 text-green-400 mr-2 mt-0.5" />
              <span>Optimized for your selected activities: {answers.activities.length} activities including
                {answers.activities.includes('4k-streaming') && ' 4K streaming'}
                {answers.activities.includes('video-calls') && ' video conferencing'}
                {answers.activities.includes('large-downloads') && ' large downloads'}
                {answers.activities.includes('cloud-backup') && ' cloud backup'}
                {answers.activities.includes('file-sharing') && ' file sharing'}
              </span>
            </li>
          )}
        </ul>
      </div>
      
      <motion.button
        className="w-full py-3 bg-green-600 text-white rounded-lg font-medium"
        onClick={handleCompleteWithSpeed}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        Show Recommended Packages
      </motion.button>
    </motion.div>
  );
};

export default RecommendationForm;